import { useCallback, useEffect, useRef, useState } from "react";
import {
  autoCorrelate,
  computeRMS,
  describePitch,
} from "../lib/audioUtils";

const DEFAULT_NOISE_GATE = 0.002; // min rise (true/pre-gain) to count a note
const FLUX_ALPHA = 0.05; // adaptation speed of the recent-rise average
const MIN_ONSET_GAP_MS = 70; // refractory -> caps at ~850 notes/min
const BUFFER_SIZE = 2048;

// Note subdivision -> how many played notes make up one quarter-note beat.
export const SUBDIVISIONS = [
  { id: "1/4", label: "1/4", factor: 1 },
  { id: "1/8", label: "1/8", factor: 2 },
  { id: "1/8T", label: "1/8T", factor: 3 },
  { id: "1/16", label: "1/16", factor: 4 },
];

const CLIP_PEAK = 0.95; // above this we are (near) clipping
const LOW_PEAK = 0.03; // below this the signal is too quiet
const GAIN_TARGET_PEAK = 0.6; // auto-gain aims to keep peaks here
const GAIN_MIN = 0.2;
const GAIN_MAX = 12;
const SILENCE_RAW = 0.012; // below this (pre-gain) we treat input as silence
const MONITOR_LEN = 12000; // ~3.5 min of samples kept for the attack graph

export function useTempoDetector() {
  const [isListening, setIsListening] = useState(false);
  const [bpm, setBpm] = useState(0);
  const [avgBpm, setAvgBpm] = useState(0);
  const [pitch, setPitch] = useState(null);
  const [level, setLevel] = useState(0);
  const [onsetCount, setOnsetCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [noteCount, setNoteCount] = useState(4);
  const [subdivision, setSubdivision] = useState("1/16");
  const [inputGain, setInputGainState] = useState(1);
  const [autoGain, setAutoGain] = useState(false);
  const [signalStatus, setSignalStatus] = useState("idle"); // idle|low|good|clip
  const [onsetFactor, setOnsetFactorState] = useState(2);
  const [noiseGate, setNoiseGateState] = useState(DEFAULT_NOISE_GATE);

  const noteCountRef = useRef(4);
  const subdivisionRef = useRef("1/16");
  const autoGainRef = useRef(false);
  const onsetFactorRef = useRef(2);
  const noiseGateRef = useRef(DEFAULT_NOISE_GATE);
  const monitorRef = useRef([]);
  const gainNodeRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const dataRef = useRef(new Float32Array(BUFFER_SIZE));

  const prevRmsRef = useRef(0);
  const fluxAvgRef = useRef(0);
  const lastOnsetRef = useRef(0);
  const onsetTimesRef = useRef([]);
  const sessionFirstRef = useRef(0);
  const sessionCountRef = useRef(0);
  const envRef = useRef([]); // {t, v:flux} envelope for tempo autocorrelation
  const lastTempoCalcRef = useRef(0);
  const avgBpmEmaRef = useRef(0);

  useEffect(() => {
    noteCountRef.current = noteCount;
    computeTempo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteCount]);

  useEffect(() => {
    subdivisionRef.current = subdivision;
    computeTempo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdivision]);

  useEffect(() => {
    autoGainRef.current = autoGain;
  }, [autoGain]);

  const setInputGain = useCallback((value) => {
    setInputGainState(value);
    if (gainNodeRef.current) gainNodeRef.current.gain.value = value;
  }, []);

  const setOnsetFactor = useCallback((value) => {
    setOnsetFactorState(value);
    onsetFactorRef.current = value;
  }, []);

  const setNoiseGate = useCallback((value) => {
    setNoiseGateState(value);
    noiseGateRef.current = value;
  }, []);

  const subFactor = useCallback(() => {
    const sub =
      SUBDIVISIONS.find((s) => s.id === subdivisionRef.current) ||
      SUBDIVISIONS[0];
    return sub.factor;
  }, []);

  // Tempo via AUTOCORRELATION of the flux (rise) envelope. This finds the
  // periodicity of the playing regardless of whether every individual onset was
  // marked -> robust to missed/soft alternate notes that break onset-counting
  // (the cause of half-tempo readings). We then bias to the FUNDAMENTAL period
  // (shortest strong peak) to avoid half/double-tempo octave errors.
  const computeTempo = useCallback(() => {
    const all = envRef.current;
    const total = all.length;
    if (total < 60) return;
    // Analysis window length grows with "Notes used for tempo" (steadier vs faster).
    const windowMs = Math.min(6000, Math.max(2500, noteCountRef.current * 800));
    const tEnd = all[total - 1].t;
    let startIdx = 0;
    for (let i = total - 1; i >= 0; i--) {
      if (tEnd - all[i].t > windowMs) {
        startIdx = i + 1;
        break;
      }
    }
    const env = all.slice(startIdx);
    const n = env.length;
    if (n < 40) return;
    const dt = (env[n - 1].t - env[0].t) / (n - 1);
    if (!(dt > 0)) return;

    let mean = 0;
    for (let i = 0; i < n; i++) mean += env[i].v;
    mean /= n;
    const v = new Array(n);
    for (let i = 0; i < n; i++) v[i] = env[i].v - mean;

    const lagMin = Math.max(3, Math.round(70 / dt)); // note period >= 70ms
    const lagMax = Math.min(n - 2, Math.round(1500 / dt)); // <= 1.5s
    if (lagMax <= lagMin) return;

    const corr = [];
    let bestLag = -1;
    let bestCorr = 0;
    for (let lag = lagMin; lag <= lagMax; lag++) {
      let c = 0;
      for (let i = lag; i < n; i++) c += v[i] * v[i - lag];
      c /= n - lag;
      corr[lag] = c;
      if (c > bestCorr) {
        bestCorr = c;
        bestLag = lag;
      }
    }
    if (bestLag < 0 || bestCorr <= 0) return;

    // Prefer the fundamental: smallest lag that is a strong local peak.
    let chosen = bestLag;
    const thresh = bestCorr * 0.8;
    for (let lag = lagMin + 1; lag < bestLag; lag++) {
      if (
        corr[lag] >= thresh &&
        corr[lag] >= corr[lag - 1] &&
        corr[lag] >= corr[lag + 1]
      ) {
        chosen = lag;
        break;
      }
    }

    const notePeriod = chosen * dt; // ms between notes
    const beatMs = notePeriod * subFactor();
    let value = Math.round(60000 / beatMs);
    value = Math.max(30, Math.min(300, value));
    setBpm(value);

    if (avgBpmEmaRef.current <= 0) avgBpmEmaRef.current = value;
    else avgBpmEmaRef.current = avgBpmEmaRef.current * 0.9 + value * 0.1;
    setAvgBpm(Math.round(avgBpmEmaRef.current));
  }, [subFactor]);

  const registerOnset = useCallback((time) => {
    onsetTimesRef.current.push(time);
    if (onsetTimesRef.current.length > 32) {
      onsetTimesRef.current = onsetTimesRef.current.slice(-32);
    }
    if (sessionCountRef.current === 0) sessionFirstRef.current = time;
    sessionCountRef.current += 1;
    setOnsetCount((c) => c + 1);
  }, []);

  const loop = useCallback(() => {
    const analyser = analyserRef.current;
    const ctx = audioCtxRef.current;
    if (!analyser || !ctx) return;

    const buf = dataRef.current;
    analyser.getFloatTimeDomainData(buf);

    const rms = computeRMS(buf);
    setLevel(rms);

    // Peak (for gain/clip handling) uses the raw sample magnitude.
    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const a = Math.abs(buf[i]);
      if (a > peak) peak = a;
    }

    // Auto-gain: nudge the input gain so peaks sit around the target -- but do
    // NOT chase silence upward (that just amplifies string/room noise). Only
    // allow gain to INCREASE when the true (pre-gain) signal is clearly present.
    const gainNode = gainNodeRef.current;
    const gain = gainNode ? gainNode.gain.value : 1;
    const rawPeak = peak / gain; // true input peak before makeup gain
    if (gainNode && autoGainRef.current && peak > 0.001) {
      const desired = (GAIN_TARGET_PEAK / peak) * gain;
      const clamped = Math.max(GAIN_MIN, Math.min(GAIN_MAX, desired));
      const wantsIncrease = clamped > gain;
      // Freeze upward gain while essentially silent; always allow pull-down.
      if (!(wantsIncrease && rawPeak < SILENCE_RAW)) {
        const rate = peak >= CLIP_PEAK ? 0.4 : 0.04;
        const next = gain + (clamped - gain) * rate;
        gainNode.gain.value = next;
        setInputGainState(Math.round(next * 100) / 100);
      }
    }

    setSignalStatus(
      peak >= CLIP_PEAK ? "clip" : peak < LOW_PEAK ? "low" : "good"
    );

    const now = performance.now();

    // Onset detection on the TRUE (pre-gain) signal via ENERGY-FLUX peak-picking
    // against a RECENT-RISE-ACTIVITY threshold (not the level). Between notes the
    // rise activity is ~0, so the bar sits at the noise gate and every attack --
    // even a small in-between one during fast/continuous playing -- clears it.
    // A long/held note has ONE rise (its attack); its smooth decay has no rise,
    // so it counts once. Two bars: recent-average * Sensitivity, and an absolute
    // Noise-gate floor that rejects tiny string/room-noise rises.
    const rawRms = rms / gain;
    const rawFlux = Math.max(0, rawRms - prevRmsRef.current);
    prevRmsRef.current = rawRms;
    const fluxThr = Math.max(
      noiseGateRef.current,
      fluxAvgRef.current * onsetFactorRef.current
    );
    let firedThisFrame = false;

    if (rawFlux > fluxThr && now - lastOnsetRef.current > MIN_ONSET_GAP_MS) {
      lastOnsetRef.current = now;
      firedThisFrame = true;
      registerOnset(now);

      const freq = autoCorrelate(buf, ctx.sampleRate);
      const described = describePitch(freq);
      if (described) {
        setPitch(described);
        setHistory((h) => {
          const next = [
            ...h,
            {
              index: h.length + 1,
              bpm: 0, // filled below via functional access
              note: `${described.name}${described.octave}`,
              t: now,
            },
          ];
          return next.slice(-48);
        });
      }
    }

    // Adapt the recent-rise average (stays ~0 in gaps, so attacks stand out).
    fluxAvgRef.current += (rawFlux - fluxAvgRef.current) * FLUX_ALPHA;

    // Record a sample for the live attack-monitor graph.
    const mon = monitorRef.current;
    mon.push({ rms: rawRms, flux: rawFlux, fluxThr, onset: firedThisFrame });
    if (mon.length > MONITOR_LEN) mon.shift();

    // Feed the flux envelope and recompute tempo (autocorrelation) on a throttle.
    const env = envRef.current;
    env.push({ t: now, v: rawFlux });
    if (env.length > 500) env.shift();
    if (now - lastTempoCalcRef.current > 150) {
      lastTempoCalcRef.current = now;
      computeTempo();
    }

    rafRef.current = requestAnimationFrame(loop);
  }, [registerOnset, computeTempo]);

  // Keep history bpm column in sync with latest bpm value
  useEffect(() => {
    if (bpm > 0) {
      setHistory((h) => {
        if (h.length === 0) return h;
        const copy = [...h];
        copy[copy.length - 1] = { ...copy[copy.length - 1], bpm };
        return copy;
      });
    }
  }, [bpm]);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const gainNode = ctx.createGain();
      gainNode.gain.value = inputGain;
      gainNodeRef.current = gainNode;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = BUFFER_SIZE;
      source.connect(gainNode);
      gainNode.connect(analyser);
      analyserRef.current = analyser;

      onsetTimesRef.current = [];
      lastOnsetRef.current = 0;
      prevRmsRef.current = 0;
      fluxAvgRef.current = 0;
      monitorRef.current = [];
      envRef.current = [];
      lastTempoCalcRef.current = 0;
      avgBpmEmaRef.current = 0;
      sessionFirstRef.current = 0;
      sessionCountRef.current = 0;
      setOnsetCount(0);
      setBpm(0);
      setAvgBpm(0);
      setPitch(null);
      setHistory([]);

      setIsListening(true);
      rafRef.current = requestAnimationFrame(loop);
    } catch (e) {
      setError(
        e && e.name === "NotAllowedError"
          ? "Microphone access denied. Please allow mic access and try again."
          : "Could not access the microphone."
      );
      setIsListening(false);
    }
  }, [loop]);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    gainNodeRef.current = null;
    setIsListening(false);
    setLevel(0);
    setSignalStatus("idle");
  }, []);

  useEffect(() => () => stop(), [stop]);

  return {
    isListening,
    bpm,
    avgBpm,
    pitch,
    level,
    onsetCount,
    history,
    error,
    noteCount,
    setNoteCount,
    subdivision,
    setSubdivision,
    inputGain,
    setInputGain,
    autoGain,
    setAutoGain,
    signalStatus,
    onsetFactor,
    setOnsetFactor,
    noiseGate,
    setNoiseGate,
    monitorRef,
    start,
    stop,
  };
}
